import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { BadgeCheck, Package, RotateCcw, Store } from "lucide-react";
import { useVehicle } from "../contexts/VehicleContext";
import { useTheme } from "../contexts/ThemeContext";
import engineCatPng from "../assets/img/parts-categories/Settings Icon Android Lollipop - 480x480.png";
import brakesCatPng from "../assets/img/parts-categories/brakes.png";
import suspensionCatPng from "../assets/img/parts-categories/cf5790bfe22b71c783f0b8ec1c25c253.png";
import electricalCatPng from "../assets/img/parts-categories/408969ad465d4334f709d40010a3ac33.png";
import bodyCatPng from "../assets/img/parts-categories/46a2b00833bd5148275d4ae27c188ce1.png";
import filtersCatPng from "../assets/img/parts-categories/5d3fd9b3d7f9f999bdbdd82d2b9ce9ed.png";
import transmissionCatPng from "../assets/img/parts-categories/03fd0e5fe5ce4cae47db7ae62ad57ad6.png";
import lubricantsCatPng from "../assets/img/parts-categories/a25f17004b406c48863abd34bf7ed5fb.png";

const CATEGORY_SELECTION_KEY = "sparepe-parts-category-selection";

function normalizeCategoryPath(path) {
  if (!path || typeof path !== "string") return "";
  return path.split("?")[0].replace(/\/$/, "") || "";
}

/**
 * Reference-matched “Parts by category” — dark shell (#121212 / #1c1c1e), cards #242426,
 * top-edge highlight, screw-hole; category art is PNG (see assets/img/parts-categories).
 */
const CATEGORY_ITEMS = [
  {
    label: "Engine",
    href: "/catalog/engine/",
    imageSrc: engineCatPng,
    glow: "drop-shadow(0 0 6px rgba(168,85,247,0.75)) drop-shadow(0 0 14px rgba(139,92,246,0.45))",
  },
  {
    label: "Brakes",
    href: "/catalog/brakes/",
    imageSrc: brakesCatPng,
    glow: "drop-shadow(0 0 6px rgba(251,113,133,0.85)) drop-shadow(0 0 16px rgba(244,63,94,0.5))",
  },
  {
    label: "Suspension",
    href: "/catalog/suspension/",
    imageSrc: suspensionCatPng,
    glow: "drop-shadow(0 0 5px rgba(226,232,240,0.55)) drop-shadow(0 0 12px rgba(148,163,184,0.4))",
  },
  {
    label: "Electrical",
    href: "/catalog/electric_components/",
    imageSrc: electricalCatPng,
    glow: "drop-shadow(0 0 6px rgba(251,146,60,0.9)) drop-shadow(0 0 16px rgba(234,88,12,0.45))",
  },
  {
    label: "Body",
    href: "/catalog/body/",
    imageSrc: bodyCatPng,
    glow: "drop-shadow(0 0 6px rgba(248,113,113,0.85)) drop-shadow(0 0 14px rgba(239,68,68,0.45))",
  },
  {
    label: "Filters",
    href: "/catalog/filters/",
    imageSrc: filtersCatPng,
    /** Asset is very dark; invert + glow so it reads on the dark tile (swap PNG anytime). */
    imageFilter:
      "invert(1) brightness(1.1) contrast(1.05) drop-shadow(0 0 10px rgba(56,189,248,0.45))",
  },
  {
    label: "Transmission",
    href: "/catalog/clutch/",
    imageSrc: transmissionCatPng,
    imageFilter:
      "brightness(1.4) contrast(1.06) drop-shadow(0 0 6px rgba(248,113,113,0.85)) drop-shadow(0 0 14px rgba(220,38,38,0.45))",
  },
  {
    label: "Lubricants",
    href: "/catalog/oilsfluids/",
    imageSrc: lubricantsCatPng,
    imageFilter:
      "brightness(1.2) contrast(1.05) drop-shadow(0 0 6px rgba(56,189,248,0.85)) drop-shadow(0 0 16px rgba(14,165,233,0.45))",
  },
  {
    label: "Steering",
    href: "/catalog/steering/",
    imageSrc: suspensionCatPng,
    glow: "drop-shadow(0 0 6px rgba(251,191,36,0.85)) drop-shadow(0 0 14px rgba(245,158,11,0.45))",
  },
  {
    label: "Cooling",
    href: "/catalog/cooling_system/",
    imageSrc: engineCatPng,
    glow: "drop-shadow(0 0 6px rgba(45,212,191,0.85)) drop-shadow(0 0 14px rgba(20,184,166,0.45))",
  },
];

const TRUST_ITEMS = [
  {
    title: "OEM verified parts",
    body: "100% genuine, sourced from OEM dealers.",
    Icon: BadgeCheck,
    iconWrap: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/25",
    iconWrapLight: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  },
  {
    title: "500+ verified garages",
    body: "Certified mechanics across 40+ cities.",
    Icon: Store,
    iconWrap: "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/25",
    iconWrapLight: "bg-sky-50 text-sky-800 ring-1 ring-sky-200",
  },
  {
    title: "30-day returns",
    body: "Hassle-free returns, no questions asked.",
    Icon: RotateCcw,
    iconWrap: "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/25",
    iconWrapLight: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  },
  {
    title: "Next-day delivery",
    body: "Metro cities · T+2 for Tier 2.",
    Icon: Package,
    iconWrap: "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/25",
    iconWrapLight: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  },
];

function CategoryImage({ item, isDark }) {
  const { imageSrc, glow, imageFilter } = item;
  let raw = imageFilter ?? glow;
  if (!isDark && imageFilter && String(imageFilter).includes("invert")) {
    raw = glow;
  }
  const filterCss = typeof raw === "string" && raw.trim() ? raw.trim() : "";
  const style = filterCss ? { filter: filterCss } : undefined;

  return (
    <img
      src={imageSrc}
      alt=""
      width={48}
      height={48}
      loading="lazy"
      draggable={false}
      className="h-10 w-10 select-none object-contain sm:h-11 sm:w-11 md:h-12 md:w-12"
      style={style}
      aria-hidden
    />
  );
}

export default function PartsByCategoryHome() {
  const location = useLocation();
  const { selectedVehicle } = useVehicle();
  const { isDark } = useTheme();
  const reduceMotion = useReducedMotion();

  const readStoredCategory = useCallback(() => {
    try {
      return normalizeCategoryPath(sessionStorage.getItem(CATEGORY_SELECTION_KEY) || "");
    } catch {
      return "";
    }
  }, []);

  const [selectedCategoryPath, setSelectedCategoryPath] = useState(readStoredCategory);

  useEffect(() => {
    if (location.pathname !== "/") return;
    setSelectedCategoryPath(readStoredCategory());
  }, [location.pathname, location.key, readStoredCategory]);

  const rememberCategorySelection = useCallback((href) => {
    const key = normalizeCategoryPath(href);
    try {
      sessionStorage.setItem(CATEGORY_SELECTION_KEY, key);
    } catch {
      /* ignore */
    }
    setSelectedCategoryPath(key);
  }, []);

  const vehicleQuery = useMemo(() => {
    if (!selectedVehicle) return "";
    const make = selectedVehicle.make || selectedVehicle.vehicleMake || "";
    const model = selectedVehicle.model || selectedVehicle.vehicleModel || "";
    const year = selectedVehicle.year || selectedVehicle.vehicleYear || "";
    const variant = selectedVehicle.variant || selectedVehicle.vehicleVariant || "";
    const params = new URLSearchParams();
    if (make) params.set("vehicleMake", make);
    if (model) params.set("vehicleModel", model);
    if (year) params.set("vehicleYear", year);
    if (variant) params.set("vehicleVariant", variant);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [selectedVehicle]);

  const hrefWithQuery = (href) => {
    if (!vehicleQuery) return href;
    const sep = href.includes("?") ? "&" : "?";
    return `${href.replace(/\/$/, "")}${sep}${vehicleQuery.slice(1)}`;
  };

  return (
    <section
      className={`relative left-1/2 z-[1] w-screen max-w-[100vw] -translate-x-1/2 overflow-x-hidden py-4 sm:py-5 md:py-6 section ${
        isDark ? "bg-sparepe-deep" : "bg-slate-100"
      }`}
    >
      <div
        className={`w-full border-y shadow-lg ${
          isDark
            ? "border-sparepe-border-md bg-sparepe-card shadow-sp-premium"
            : "border-gray-200 bg-white shadow-[0_12px_40px_-12px_rgba(15,23,42,0.08)]"
        }`}
      >
        <div
          className={`sec-hrow flex items-end justify-between gap-3 border-b px-4 py-3.5 sm:px-6 sm:py-4 md:px-8 md:py-4.5 lg:px-10 ${
            isDark ? "border-sparepe-border" : "border-gray-100"
          }`}
        >
          <h2
            className={`sec-title text-left sm:text-[11px] md:text-xs ${
              isDark ? "sp-section-label" : "text-[10px] font-medium uppercase tracking-[0.22em] text-slate-500"
            }`}
          >
            Parts by category
          </h2>
          <Link
            to={vehicleQuery ? `/category${vehicleQuery}` : "/category"}
            className={`see-all shrink-0 text-xs font-bold transition-colors sm:text-sm ${
              isDark ? "text-sparepe-accent hover:text-sparepe-accent-dk" : "text-blue-700 hover:text-blue-600 md:text-base"
            }`}
          >
            View all categories →
          </Link>
        </div>

        <div
          className="grid grid-cols-2 justify-items-center gap-3 p-3.5 sm:grid-cols-3 md:grid-cols-5 sm:gap-4 sm:p-5 md:gap-5 md:p-6 lg:gap-6 lg:p-8 [perspective:1200px]"
          style={{ transformStyle: "preserve-3d" }}
        >
          {CATEGORY_ITEMS.map((item, index) => {
            const { label, href } = item;
            const basePath = normalizeCategoryPath(href);
            const isSelected = selectedCategoryPath === basePath;
            const cardClass = isDark
              ? isSelected
                ? "border border-[#2a2c34] border-t-black/55 border-l-black/45 border-b-[#383c48] border-r-[#383c48] bg-gradient-to-b from-[#24262e] via-[#1c1e26] to-[#14151a] shadow-[inset_0_5px_14px_rgba(0,0,0,0.6),inset_0_-2px_8px_rgba(255,255,255,0.03),inset_3px_3px_8px_rgba(0,0,0,0.45),0_2px_4px_rgba(0,0,0,0.35)]"
                : "border border-[#3a3a40] bg-gradient-to-b from-[#323236] via-[#28282c] to-[#1a1a1d] shadow-[0_1px_0_rgba(255,255,255,0.09)_inset,0_-10px_24px_-6px_rgba(0,0,0,0.55)_inset,0_14px_28px_-6px_rgba(0,0,0,0.75),0_6px_0_0_rgba(10,10,12,0.95),0_20px_40px_-12px_rgba(0,0,0,0.65)] hover:border-[#4d4d56] hover:shadow-[0_1px_0_rgba(255,255,255,0.11)_inset,0_-10px_28px_-6px_rgba(0,0,0,0.6)_inset,0_22px_48px_-8px_rgba(0,0,0,0.8),0_8px_0_0_rgba(8,8,10,0.98),0_28px_56px_-14px_rgba(0,0,0,0.7)]"
              : isSelected
                ? "border-2 border-orange-400 bg-gradient-to-b from-white to-slate-50 shadow-md ring-2 ring-orange-100"
                : "border border-gray-200 bg-white shadow-md hover:border-gray-300 hover:shadow-lg";
            return (
              <motion.div
                key={label}
                className="transform-gpu mx-auto w-full max-w-[19rem]"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
                whileHover={
                  isSelected
                    ? false
                    : {
                        y: -10,
                        rotateX: 3,
                        z: 20,
                        transition: { type: "spring", stiffness: 420, damping: 24 },
                      }
                }
                style={{ transformStyle: "preserve-3d" }}
              >
                <Link
                  to={hrefWithQuery(href)}
                  onPointerDownCapture={() => rememberCategorySelection(href)}
                  onClick={() => rememberCategorySelection(href)}
                  className={`group relative flex w-full flex-col items-center overflow-hidden rounded-2xl px-2 pb-5 pt-6 transition-[border-color,box-shadow] duration-300 sm:rounded-[1.2rem] sm:pb-6 sm:pt-7 md:pb-7 md:pt-8 ${cardClass}`}
                >
                  {isDark ? (
                    <>
                      <span
                        className="pointer-events-none absolute inset-y-0 left-0 w-px bg-gradient-to-b from-white/[0.14] via-transparent to-transparent"
                        aria-hidden
                      />
                      <span
                        className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-black/40 to-black/70"
                        aria-hidden
                      />
                      <span
                        className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-white/[0.18] to-transparent"
                        aria-hidden
                      />
                    </>
                  ) : null}
                  <span
                    className={`pointer-events-none absolute inset-0 bg-gradient-to-br via-transparent to-transparent ${
                      isDark
                        ? `from-white/[0.06] ${isSelected ? "opacity-35" : "opacity-70"}`
                        : "from-white/80 opacity-100"
                    }`}
                    aria-hidden
                  />
                  {/* Bottom depth (receding plane) */}
                  {isDark ? (
                  <span
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/45 via-black/15 to-transparent"
                    aria-hidden
                  />
                  ) : null}
                  {/* Indicator: dark dimple (idle) or glowing green LED (selected, e.g. Brakes) */}
                  {isSelected ? (
                    <span
                      className="pointer-events-none absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center sm:right-2.5 sm:top-2.5"
                      aria-hidden
                    >
                      <span className="absolute h-5 w-5 rounded-full bg-[#22c55e]/35 blur-md" />
                      <span className="absolute h-4 w-4 rounded-full bg-[#4ade80]/25 blur-sm" />
                      <span className="relative h-2.5 w-2.5 rounded-full bg-[#86efac] shadow-[0_0_8px_#4ade80,0_0_16px_rgba(74,222,128,0.95),0_0_28px_rgba(34,197,94,0.55)] ring-[3px] ring-[#bbf7d0]/40" />
                    </span>
                  ) : (
                    <span
                      className={`pointer-events-none absolute right-3 top-3 z-10 h-2 w-2 rounded-full sm:right-3.5 sm:top-3.5 ${
                        isDark
                          ? "bg-[#101011] shadow-[inset_0_2px_4px_rgba(0,0,0,0.95),inset_0_-1px_1px_rgba(255,255,255,0.06)] ring-1 ring-black/70"
                          : "bg-slate-300 ring-1 ring-slate-400/80"
                      }`}
                      aria-hidden
                    />
                  )}

                  <motion.div
                    className="relative z-[1] mb-3 flex flex-1 items-center justify-center sm:mb-4"
                    animate={reduceMotion ? { y: 0 } : { y: [0, -7, 0] }}
                    transition={
                      reduceMotion
                        ? undefined
                        : {
                            duration: 2.75,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: index * 0.14,
                          }
                    }
                  >
                    <CategoryImage item={item} isDark={isDark} />
                  </motion.div>
                  <span
                    className={`relative z-[1] text-center text-[10px] font-semibold uppercase tracking-[0.14em] sm:text-[11px] md:text-xs ${
                      isDark
                        ? "text-[#ececf0] drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]"
                        : "text-slate-800"
                    }`}
                  >
                    {label}
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div
          className={`sp-trust-bar trust-bar px-4 sm:px-6 md:px-8 lg:px-10 ${
            isDark ? "" : "grid grid-cols-1 gap-3.5 border-t border-gray-100 bg-slate-50 py-4 sm:grid-cols-2 sm:gap-4 sm:py-5 md:grid-cols-4"
          }`}
        >
          {TRUST_ITEMS.map(({ title, body, Icon, iconWrap, iconWrapLight }) => (
            <div key={title} className={`sp-trust-item ti ${isDark ? "" : "flex gap-3 rounded-lg px-0.5 py-0.5 sm:gap-3"}`}>
              <div
                className={`ti-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  isDark ? `sp-trust-icon ${iconWrap}` : iconWrapLight
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p
                  className={`text-xs font-medium sm:text-sm ${
                    isDark ? "text-sparepe-ink" : "font-semibold capitalize text-slate-900"
                  }`}
                >
                  <strong>{title}</strong>
                </p>
                <p
                  className={`mt-0.5 text-[11px] leading-snug sm:text-xs ${
                    isDark ? "text-sparepe-ink-3" : "text-slate-600"
                  }`}
                >
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
